import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1MessagingChannels, MessagingChannelType } from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';

import { MessagingChannel } from '~hooks/useMessaging/models';
import {
  messagingTargetChannelQueryKey,
  MessagingTargetChannelType,
} from '~queries/messagingQueryKeys';
import reportRequestError from '~services/reportRequestError';
import { Nullable } from '~types/Nullable';

/**
 * The target filters drop archived channels, so the answer is the one live channel or none. The
 * index is membership-scoped, so a non-member also gets none; their first send resolves it.
 */
export default function useMessagingTargetChannelQuery({
  channelType,
  targetId,
}: {
  channelType: MessagingTargetChannelType;
  targetId: Nullable<string>;
}) {
  const query = useQuery({
    queryKey: messagingTargetChannelQueryKey(channelType, targetId),
    queryFn: targetId
      ? async ({ signal }) => {
          try {
            const response = await getV1MessagingChannels({
              query: {
                ...(channelType === MessagingChannelType.JOB
                  ? { 'filter[job_id]': targetId }
                  : { 'filter[order_id]': targetId }),
                'page[limit]': 1,
              },
              signal,
            });
            const [channel] = response.data.data.map(MessagingChannel.parse);

            return channel ?? null;
          } catch (error) {
            // React Query drops a read it cancelled, so there is nothing to report.
            if (axios.isCancel(error)) throw error;

            // Failing keeps a channel already on screen; with none cached, the first send opens or joins it.
            reportRequestError(
              error,
              t('error_messages.messaging.failed_to_fetch_channel'),
            );

            throw error;
          }
        }
      : skipToken,
    // Each attempt reports its own failure, and the first send recovers the channel anyway.
    retry: false,
  });

  return {
    channel: query.data ?? null,
    isError: query.isError,
    isLoading: query.isLoading,
    isRefreshing: query.isRefetching,
  };
}
